import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { useModals } from '@ncc-frontend/core';
import _ from 'lodash';

import { FileStatus, TCompleteFile } from './vv-types';
import { MAX_FILE_SIZE_BYTES } from './constants';
import FileUploadQueue from './file-queue';
import NewUploadCard from 'lib-ui/new-upload-card';
import OversizedFilesModal from './oversized-files-modal';
import UploadDropzone from './components/upload-dropzone';
import toastFunctions from 'lib-ui/toast/Toast';
import useAgreementsDepositsFiles from 'core/api/client-portal-v2/hooks/deposits/use-agreements-deposits-files';
import useDeleteFile from 'core/api/spectre-agent/hooks/delete-file/use-delete-file';
import useStartUpload from 'core/api/spectre-agent/use-start-upload';
import useToken from './use-token';

const VirtualV = ({ depositId }: { depositId: number }) => {
    const { push } = useModals();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const { mutate: startUpload } = useStartUpload({
        onSuccess: () => {
            // Clear the file input after completing upload
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    });

    const { isLoading: isDeleting, mutate: deleteFile } = useDeleteFile({
        onSettled(data, error, vars) {
            if (!error && data?.status === 204) {
                deleteLocalFile(vars.fileName!);
            }

            if (error && error.response?.status === 404) {
                deleteLocalFile(vars.fileName!);
            } else if (error && error.response?.status !== 404) {
                toastFunctions.error('Error deleting file. Please try again.');
            }
        }
    });

    const deleteLocalFile = (fileName: string) => {
        uploadQueueRef.current?.removeFileFromQueue(fileName);
        setFiles((prevFiles) =>
            prevFiles.filter((f) => f.fileName !== fileName)
        );
    };

    const { data: uploadedFiles } = useAgreementsDepositsFiles(depositId);

    function statusHandler(complete: boolean, verificationState: number) {
        let status;
        if (complete && verificationState === 1) {
            status = 'completed';
        }
        if (!complete && verificationState === 0) {
            status = 'incomplete';
        }
        return status;
    }

    const modifiedFiles = uploadedFiles?.depositFiles.map((file) => {
        const status = statusHandler(file.completed, file.verificationState);
        return {
            ...file,
            depositId: depositId,
            retryCount: 0,
            status: status as FileStatus,
            progress: status === 'completed' ? 100 : 0
        };
    });

    const [files, setFiles] = useState<TCompleteFile[]>(modifiedFiles ?? []);
    const uploadQueueRef = useRef<FileUploadQueue | null>(null);

    const initQueue = useCallback(() => {
        if (!uploadQueueRef.current) {
            uploadQueueRef.current = new FileUploadQueue(
                startUpload,
                (file, newStatus) => {
                    setFiles((prevFiles) =>
                        prevFiles.map((f) =>
                            f.fileName === file.fileName
                                ? { ...f, status: newStatus }
                                : f
                        )
                    );
                },
                (file, progress) => {
                    setFiles((prevFiles) =>
                        prevFiles.map((f) =>
                            f.fileName === file.fileName
                                ? { ...f, progress }
                                : f
                        )
                    );
                },
                (file) => {
                    setFiles((prevFiles) =>
                        prevFiles.map((f) =>
                            f.fileName === file.fileName
                                ? { ...f, status: 'completed' as FileStatus }
                                : f
                        )
                    );
                }
            );
        }
        return uploadQueueRef.current;
    }, [startUpload]);

    const isDuplicateFile = useCallback(
        (fileName: string, fileSize: number): boolean => {
            // Check if file with same name and size already exists in our current files array
            const existsInQueue = files.some(
                (f) => f.fileName === fileName && f.fileSize === fileSize
            );

            // Also check in already uploaded files if available
            const existsInUploaded =
                uploadedFiles?.depositFiles.some(
                    (f) => f.fileName === fileName && f.fileSize === fileSize
                ) || false;

            return existsInQueue || existsInUploaded;
        },
        [files, uploadedFiles]
    );

    const handleSelectedFiles = (
        event: ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLLabelElement>
    ) => {
        event.preventDefault();

        const isChangeEvent =
            'target' in event && event.target instanceof HTMLInputElement;

        const filesSelected = isChangeEvent
            ? Array.from((event.target as HTMLInputElement).files || [])
            : Array.from(
                  (event as React.DragEvent<HTMLLabelElement>).dataTransfer
                      .files || []
              );

        const filteredFiles = filesSelected.filter((file) => {
            if (file.size > MAX_FILE_SIZE_BYTES) {
                push(OversizedFilesModal, { isOnPrem: false });
                return false; // Exclude the file from the filtered array
            }

            if (isDuplicateFile(file.name, file.size)) {
                toastFunctions.error(
                    `File "${file.name}" is already queued or uploaded.`,
                    'duration-600'
                );
                return false;
            }

            return true; // Include the file in the filtered array
        });

        const mutatedObjectArray = filteredFiles.map((file: File) => ({
            depositId: depositId,
            file: file,
            fileName: file.name,
            fileSize: file.size,
            retryCount: 0,
            status: 'queued' as FileStatus,
            progress: 0,
            completed: false
        }));

        const queue = initQueue();

        mutatedObjectArray.forEach((file) => {
            setFiles((prevFiles) => [file, ...prevFiles]);
            queue.enqueue(file);
        });
    };
    type TempStatus = 'uploading' | 'queued' | 'completed' | 'incomplete';

    const sortedFiles = [...files].sort((a, b) => {
        // Define priority values for each status
        const statusPriority: Record<TempStatus, number> = {
            uploading: 0, // highest priority
            queued: 1, // medium priority
            incomplete: 2, // lowest priority
            completed: 3 // lowest priority
            // Add other statuses as needed
        };
        if (['deleting', 'failed'].includes(a.status)) return 0;
        if (['deleting', 'failed'].includes(b.status)) return 0;

        // Compare based on status priority
        return (
            _.get(statusPriority, a.status, b.status) -
            _.get(statusPriority, b.status)
        );
    });

    return (
        <div className="grid grid-cols-1 gap-6">
            <UploadDropzone
                onFilesSelected={handleSelectedFiles}
                ref={fileInputRef}
            />
            <div className="space-y-2.5">
                {sortedFiles.length > 0 &&
                    sortedFiles.map((file) => (
                        <NewUploadCard
                            key={`${file.fileName}_${file.fileSize}`}
                            file={file}
                            progress={file.progress}
                            onDeleteFile={() => {
                                if (isDeleting) return;
                                setFiles((prevFiles) =>
                                    prevFiles.map((f) =>
                                        f.fileName === file.fileName
                                            ? {
                                                  ...f,
                                                  status: 'deleting' as FileStatus
                                              }
                                            : f
                                    )
                                );
                                if (
                                    file.status === 'uploading' &&
                                    uploadQueueRef.current
                                ) {
                                    uploadQueueRef.current.stopUpload();
                                }
                                // if file is local only we don't need to execute
                                // deleteFile hook only local deletion
                                if (file.status === 'queued') {
                                    deleteLocalFile(file.fileName!);
                                } else {
                                    deleteFile(file);
                                }
                            }}
                        />
                    ))}
            </div>
        </div>
    );
};

function VVContainer({ depositId }: { depositId: number }) {
    const { isTokenLoading } = useToken(depositId);

    return isTokenLoading ? (
        <div className="h-full flex flex-col items-center justify-center">
            <FontAwesomeIcon
                className="h-14 w-14 mt-40 mx-auto"
                icon={faSpinner}
                spin
            />
        </div>
    ) : (
        <VirtualV depositId={depositId} />
    );
}

export default VVContainer;
